(function armorFinderV2Bootstrap() {
  'use strict';

  var GLOBAL_KEY = 'ArmorFinderV2';
  var ROOT_SELECTOR = '[data-armor-finder-v2]';
  var FORMULAS = ['red', 'blue', 'green'];
  var HELP_URL = '/pages/find-my-formula#TireHelp';
  var DOSING_URL = '/pages/dosing-chart';
  var INSTALL_URL = '/pages/install';

  if (window[GLOBAL_KEY] && typeof window[GLOBAL_KEY].refresh === 'function') {
    window[GLOBAL_KEY].refresh(document);
    return;
  }

  var instances = new WeakMap();
  var authorityCache = new Map();

  function rootsWithin(container) {
    var roots = [];
    if (!container) return roots;
    if (container.matches && container.matches(ROOT_SELECTOR)) roots.push(container);
    if (container.querySelectorAll) {
      roots = roots.concat(Array.prototype.slice.call(container.querySelectorAll(ROOT_SELECTOR)));
    }
    return roots;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function normalizeText(value) {
    return String(value == null ? '' : value)
      .trim()
      .replace(/\s+/g, ' ')
      .toLocaleLowerCase();
  }

  function clone(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function invariant(condition, message) {
    if (!condition) throw new Error(message);
  }

  async function sha256Text(value) {
    invariant(
      window.crypto && window.crypto.subtle && window.TextEncoder,
      'Cryptographic verification is unavailable.'
    );
    var bytes = new TextEncoder().encode(value);
    var digest = await window.crypto.subtle.digest('SHA-256', bytes);
    return Array.prototype.map
      .call(new Uint8Array(digest), function toHex(byte) {
        return byte.toString(16).padStart(2, '0');
      })
      .join('');
  }

  function jsonHash(value) {
    return sha256Text(JSON.stringify(value) + '\n');
  }

  function formulaRulesFor(root) {
    var script = root.querySelector('[data-armor-formula-rules-v2]');
    invariant(script, 'The formula-rule mirror is missing.');
    var rules = JSON.parse(script.textContent);
    invariant(rules && rules.schema_version === 2, 'The formula-rule mirror is not current.');
    invariant(rules.model_authority, 'The formula-rule authority is missing.');
    invariant(Array.isArray(rules.rules) && rules.rules.length, 'The formula rules are missing.');
    invariant(rules.products, 'The formula product map is missing.');
    FORMULAS.forEach(function requireFormula(formula) {
      var product = rules.products[formula];
      invariant(product, 'A formula product is missing.');
      invariant(
        typeof product['handle'] === 'string' &&
          /^[a-z0-9][a-z0-9-]*$/.test(product['handle']),
        'A formula product handle is invalid.'
      );
      invariant(
        product['url'] === '/products/' + product['handle'],
        'A formula product URL does not match its handle.'
      );
      invariant(
        typeof product['name'] === 'string' && product['name'].trim(),
        'A formula product name is missing.'
      );
    });
    return rules;
  }

  function formulaRuleSafetyProjection(rules) {
    return rules.rules.map(function projectRule(rule) {
      var result = rule && rule.result ? rule.result : {};
      return {
        id: rule && rule.id,
        priority: rule && rule.priority,
        when: rule && rule.when,
        result: {
          type: result.type,
          fit_status: result.fit_status,
          formula: result.formula == null ? null : result.formula,
          secondary_formula:
            result.secondary_formula == null ? null : result.secondary_formula,
          recommendation_authority:
            result.recommendation_authority == null
              ? null
              : result.recommendation_authority
        }
      };
    });
  }

  function formulaProductSafetyProjection(rules) {
    return FORMULAS.map(function projectProduct(formula) {
      var product = rules.products[formula];
      return {
        formula: formula,
        handle: product['handle'],
        url: product['url'],
        name: product['name']
      };
    });
  }

  function formulaRuntimeSafetyProjection(rules) {
    return {
      rules: formulaRuleSafetyProjection(rules),
      products: formulaProductSafetyProjection(rules)
    };
  }

  function authorityKey(root) {
    return [
      root.dataset.modelUrl,
      root.dataset.exactUrl,
      root.dataset.modelPayloadSha256,
      root.dataset.algorithmSha256,
      root.dataset.schemaSha256,
      root.dataset.exactFileSha256,
      root.dataset.exactRowsSha256,
      root.dataset.formulaSafetySha256
    ].join('|');
  }

  async function fetchText(url) {
    var response = await window.fetch(url, {
      cache: 'force-cache',
      credentials: 'same-origin'
    });
    invariant(response.ok, 'A current authority file did not load.');
    return response.text();
  }

  async function loadAndVerifyAuthority(root) {
    var key = authorityKey(root);
    if (authorityCache.has(key)) return authorityCache.get(key);

    var promise = (async function verify() {
      var texts = await Promise.all([
        fetchText(root.dataset.modelUrl),
        fetchText(root.dataset.exactUrl)
      ]);
      var modelText = texts[0];
      var exactText = texts[1];
      var model = JSON.parse(modelText);
      var exactData = JSON.parse(exactText);

      invariant(model.release_state === 'released', 'The measured-tire model is not released.');
      invariant(model.schema_id === 'armor-tire-fit-model-v2', 'The model schema is unexpected.');
      invariant(model.authority === 'dosing.dimensional_calculator', 'The model authority is unexpected.');
      invariant(model.integrity && model.dependencies, 'The model integrity record is incomplete.');

      var payload = clone(model);
      delete payload.integrity.model_payload_sha256;
      payload.release_state = 'candidate_independent_review_pending';

      var hashes = await Promise.all([
        jsonHash(payload),
        jsonHash(model.algorithm),
        jsonHash({
          input_contract: model.input_contract,
          output_contract: model.output_contract
        }),
        sha256Text(exactText),
        jsonHash(exactData.rows)
      ]);

      invariant(
        hashes[0] === root.dataset.modelPayloadSha256 &&
          hashes[0] === model.integrity.model_payload_sha256,
        'The measured-tire model payload did not verify.'
      );
      invariant(
        hashes[1] === root.dataset.algorithmSha256 &&
          hashes[1] === model.integrity.algorithm_sha256,
        'The measured-tire algorithm did not verify.'
      );
      invariant(
        hashes[2] === root.dataset.schemaSha256 &&
          hashes[2] === model.integrity.schema_contract_sha256,
        'The measured-tire input/output contract did not verify.'
      );
      invariant(
        hashes[3] === root.dataset.exactFileSha256 &&
          hashes[3] === model.dependencies.exact_rows.file_sha256,
        'The exact dosing file did not verify.'
      );
      invariant(
        hashes[4] === root.dataset.exactRowsSha256 &&
          hashes[4] === model.dependencies.exact_rows.approved_rows_sha256,
        'The exact dosing rows did not verify.'
      );
      invariant(
        Array.isArray(exactData.rows) &&
          exactData.rows.length === model.dependencies.exact_rows.row_count &&
          exactData.row_count === exactData.rows.length,
        'The exact dosing row count did not verify.'
      );

      return {
        model: model,
        exactData: exactData
      };
    })();

    authorityCache.set(key, promise);
    promise.catch(function releaseFailedCacheEntry() {
      authorityCache.delete(key);
    });
    return promise;
  }

  async function verifyFormulaMirror(root, authority, rules) {
    var model = authority.model;
    var mirror = rules.model_authority;
    invariant(
      mirror.model_payload_sha256 === root.dataset.modelPayloadSha256 &&
        mirror.model_payload_sha256 === model.integrity.model_payload_sha256,
      'The formula mirror points to another model payload.'
    );
    invariant(
      mirror.algorithm_version === model.algorithm.version &&
        mirror.algorithm_sha256 === model.integrity.algorithm_sha256,
      'The formula mirror points to another algorithm.'
    );
    invariant(
      mirror.schema_contract_sha256 === model.integrity.schema_contract_sha256,
      'The formula mirror points to another input/output contract.'
    );
    invariant(
      mirror.exact_rows_sha256 === model.dependencies.exact_rows.approved_rows_sha256,
      'The formula mirror points to another exact chart.'
    );
    invariant(
      mirror.formula_mirror_sha256 === model.integrity.formula_mirror_sha256,
      'The formula mirror integrity reference did not verify.'
    );
    invariant(
      /^[a-f0-9]{64}$/.test(root.dataset.formulaSafetySha256 || ''),
      'The formula runtime safety pin is missing.'
    );
    var hashes = await Promise.all([
      jsonHash(formulaRuleSafetyProjection(rules)),
      jsonHash(formulaRuntimeSafetyProjection(rules))
    ]);
    invariant(
      hashes[0] === mirror.formula_mirror_sha256 &&
        hashes[0] === model.integrity.formula_mirror_sha256,
      'The formula-rule safety projection did not verify.'
    );
    invariant(
      hashes[1] === root.dataset.formulaSafetySha256,
      'The formula product mapping did not verify.'
    );
  }

  function gcd(left, right) {
    var a = left < 0n ? -left : left;
    var rightValue = right < 0n ? -right : right;
    while (rightValue !== 0n) {
      var remainder = a % rightValue;
      a = rightValue;
      rightValue = remainder;
    }
    return a || 1n;
  }

  function fraction(numerator, denominator) {
    var n = BigInt(numerator);
    var d = denominator === undefined ? 1n : BigInt(denominator);
    invariant(d !== 0n, 'A model denominator is zero.');
    if (d < 0n) {
      n = -n;
      d = -d;
    }
    var divisor = gcd(n, d);
    return { n: n / divisor, d: d / divisor };
  }

  function fractionFromConfig(value) {
    invariant(
      value &&
        Number.isSafeInteger(value.numerator) &&
        Number.isSafeInteger(value.denominator) &&
        value.denominator > 0,
      'A model rational is invalid.'
    );
    return fraction(BigInt(value.numerator), BigInt(value.denominator));
  }

  function add(left, right) {
    return fraction(left.n * right.d + right.n * left.d, left.d * right.d);
  }

  function subtract(left, right) {
    return fraction(left.n * right.d - right.n * left.d, left.d * right.d);
  }

  function multiply(left, right) {
    return fraction(left.n * right.n, left.d * right.d);
  }

  function divide(left, right) {
    invariant(right.n !== 0n, 'A model divisor is zero.');
    return fraction(left.n * right.d, left.d * right.n);
  }

  function compare(left, right) {
    var delta = left.n * right.d - right.n * left.d;
    return delta < 0n ? -1 : delta > 0n ? 1 : 0;
  }

  function wholeOunceEstimate(model, value) {
    var rounding = model.algorithm.rounding;
    var interval = rounding.bottle_target_interval;
    var lowerOffset = fractionFromConfig(interval.lower_offset_ounces);
    var upperFraction = fractionFromConfig(interval.upper_offset_target_fraction);
    var upperMaximum = fractionFromConfig(interval.upper_offset_maximum_ounces);
    for (var i = 0; i < rounding.bottle_targets_ounces.length; i += 1) {
      var target = fraction(BigInt(rounding.bottle_targets_ounces[i]));
      var proportionalUpperOffset = multiply(target, upperFraction);
      var upperOffset = compare(proportionalUpperOffset, upperMaximum) <= 0
        ? proportionalUpperOffset
        : upperMaximum;
      if (compare(value, subtract(target, lowerOffset)) >= 0 &&
          compare(value, add(target, upperOffset)) <= 0) return target;
    }
    // Round the original estimate once; snapping an already-rounded value
    // would make a second adjustment beyond the owner's bounded intervals.
    var nearest = fraction((2n * value.n + value.d) / (2n * value.d));
    var minimum = fractionFromConfig(rounding.minimum_ounces);
    return compare(nearest, minimum) < 0 ? minimum : nearest;
  }

  function canonicalDecimal(value) {
    var text = String(value == null ? '' : value).trim();
    if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(text)) return null;
    var parts = text.split('.');
    var whole = (parts[0] || '0').replace(/^0+(?=\d)/, '');
    var decimal = (parts[1] || '').replace(/0+$/, '');
    var canonical = decimal ? whole + '.' + decimal : whole;
    if (!/^(?:0|[1-9]\d*)(?:\.\d*[1-9])?$/.test(canonical)) return null;
    return canonical;
  }

  function parseCanonicalDecimal(value) {
    invariant(
      typeof value === 'string' && /^(?:0|[1-9]\d*)(?:\.\d*[1-9])?$/.test(value),
      'A measurement is not a canonical decimal.'
    );
    var parts = value.split('.');
    var decimal = parts[1] || '';
    var denominator = 10n ** BigInt(decimal.length);
    var numerator = BigInt(parts[0]) * denominator + BigInt(decimal || '0');
    return fraction(numerator, denominator);
  }

  function toNumber(value) {
    var numeric = Number(value.n) / Number(value.d);
    invariant(Number.isFinite(numeric), 'The recommendation is not finite.');
    return numeric;
  }

  function conversionFor(model, family, unit) {
    var conversions =
      family === 'dimension'
        ? model.algorithm.dimension_unit_conversions_to_in
        : model.algorithm.speed_unit_conversions_to_mph;
    invariant(Object.prototype.hasOwnProperty.call(conversions, unit), 'The selected unit is unsupported.');
    return fractionFromConfig(conversions[unit]);
  }

  function measurementToUnit(model, measurement, family) {
    invariant(measurement && typeof measurement === 'object', 'A measurement is missing.');
    var parsed = parseCanonicalDecimal(measurement.value);
    invariant(parsed.n > 0n, 'A measurement must be positive.');
    return multiply(parsed, conversionFor(model, family, measurement.unit));
  }

  function within(value, bounds) {
    return (
      compare(value, fractionFromConfig(bounds.min)) >= 0 &&
      compare(value, fractionFromConfig(bounds.max)) <= 0
    );
  }

  function dimensionalTerm(model, diameter, width) {
    return multiply(
      multiply(diameter, width),
      fraction(
        BigInt(model.algorithm.dimensional_term.outside_diameter_times_installed_width_numerator),
        BigInt(model.algorithm.dimensional_term.denominator)
      )
    );
  }

  function roundedDimensionalRecommendation(model, diameter, width, device) {
    var term = dimensionalTerm(model, diameter, width);
    var reference = fractionFromConfig(model.algorithm.reference_threshold);
    var combined = term;
    if (compare(term, reference) > 0) {
      var excess = subtract(term, reference);
      var adjustmentFactor = fraction(
        BigInt(model.algorithm.coating_allowance_above_reference.excess_numerator),
        BigInt(model.algorithm.coating_allowance_above_reference.excess_denominator)
      );
      combined = add(term, multiply(excess, adjustmentFactor));
    }
    var rounded = wholeOunceEstimate(model, combined);
    if (device === 'onewheel') {
      var onewheelFloor = model.algorithm.onewheel_final_recommendation_floor;
      invariant(
        onewheelFloor &&
          onewheelFloor.applies_to_device === 'onewheel' &&
          onewheelFloor.applies_after_rounding === true,
        'The Onewheel recommendation rule is unavailable.'
      );
      var floorAmount = fractionFromConfig(onewheelFloor.amount_ounces);
      if (compare(rounded, floorAmount) < 0) rounded = floorAmount;
    }
    return rounded;
  }

  function findEnvelope(model, device) {
    return (
      model['scope'].device_envelopes.find(function find(entry) {
        return entry.device === device;
      }) || null
    );
  }

  function checkedDimensions(model, input) {
    var hasDiameter = Object.prototype.hasOwnProperty.call(input, 'outside_diameter');
    var hasWidth = Object.prototype.hasOwnProperty.call(input, 'installed_width');
    if (!hasDiameter && !hasWidth) return { error: 'dimensions_not_supplied' };
    if (!hasDiameter || !hasWidth) return { error: 'incomplete_dimension_pair' };

    var diameter;
    var width;
    try {
      diameter = measurementToUnit(model, input.outside_diameter, 'dimension');
      width = measurementToUnit(model, input.installed_width, 'dimension');
    } catch (error) {
      return { error: 'missing_or_implausible_dimension' };
    }
    if (compare(width, diameter) >= 0) return { error: 'width_not_less_than_diameter' };
    var ratio = divide(width, diameter);
    if (!within(ratio, model['scope'].cross_dimension_bounds.width_to_diameter_ratio)) {
      return { error: 'implausible_width_to_diameter_ratio' };
    }
    var envelope = findEnvelope(model, input.device);
    if (!envelope) return { error: 'device_has_no_released_dimensional_envelope' };
    if (
      !within(diameter, envelope.outside_diameter_in) ||
      !within(width, envelope.installed_width_in)
    ) {
      return { error: 'dimensions_outside_conservative_envelope' };
    }
    var term = dimensionalTerm(model, diameter, width);
    if (!within(term, model.algorithm.dimensional_term_bounds)) {
      return { error: 'dimensional_term_outside_model_bounds' };
    }
    return {
      diameter: diameter,
      width: width
    };
  }

  function exactTireDimensions(tire) {
    var sizeMatch = String(tire).match(/^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)(?:-|$)/i);
    if (sizeMatch) {
      return {
        diameter: parseCanonicalDecimal(sizeMatch[1]),
        width: parseCanonicalDecimal(sizeMatch[2])
      };
    }
    var diameterMatch = String(tire).match(/^(\d+(?:\.\d+)?)\s+inch$/i);
    if (diameterMatch) {
      return {
        diameter: parseCanonicalDecimal(diameterMatch[1]),
        width: null
      };
    }
    return null;
  }

  function suppliedDimensionsContradictExactRow(row, checked) {
    var released = exactTireDimensions(row.tire);
    if (!released) return false;
    if (compare(checked.diameter, released.diameter) !== 0) return true;
    return released.width !== null && compare(checked.width, released.width) !== 0;
  }

  function exactDeviceForInput(device) {
    if (device === 'onewheel') return 'Onewheel / single-wheel board';
    if (device === 'scooter') return 'Electric scooter';
    return null;
  }

  function productMaps(rules) {
    var formulaToProduct = {};
    var handleToFormula = {};
    FORMULAS.forEach(function mapFormula(formula) {
      formulaToProduct[formula] = rules.products[formula];
      handleToFormula[rules.products[formula].handle] = formula;
    });
    return {
      formulaToProduct: formulaToProduct,
      handleToFormula: handleToFormula
    };
  }

  function choice(maps, formula, status) {
    return {
      status: status,
      formula: formula,
      product_handle: maps.formulaToProduct[formula].handle
    };
  }

  function formulaFit(maps, preferredFormula, secondaryFormula, reasons) {
    return {
      status: 'preferred',
      preferred: choice(maps, preferredFormula, 'preferred'),
      secondary: secondaryFormula ? choice(maps, secondaryFormula, 'secondary') : null,
      reasons: reasons,
      visual_label: 'Relative selection fit'
    };
  }

  function failure(outcome, reasonCodes) {
    return {
      outcome: outcome,
      formula_fit: {
        status: outcome === 'outside_current_scope' ? 'outside_current_scope' : 'human_review'
      },
      reason_codes: reasonCodes,
      next_step: HELP_URL
    };
  }

  function terminalOutcomeForAnswers(model, answers) {
    var device = answers.device;
    if (['automobile', 'highway_motorcycle', 'e_moto'].indexOf(device) !== -1) {
      return failure('outside_current_scope', ['device_outside_current_scope']);
    }
    if (
      answers.existing_sealant === 'present_unknown_remaining_volume' ||
      answers.existing_sealant === 'unknown'
    ) {
      return failure('human_review', [
        'existing_sealant_requires_remaining_volume_review'
      ]);
    }

    return null;
  }

  function recommendation(source, amount, fit, reasonCodes, rowId) {
    var output = {
      outcome: 'recommendation',
      source: source,
      recommended_amount: {
        value: amount,
        unit: 'US_fl_oz_per_tire'
      },
      formula_fit: fit,
      reason_codes: reasonCodes
    };
    if (rowId) output.row_id = rowId;
    return output;
  }

  function fitForCalculatedInput(model, maps, input, width, speed) {
    var speedBoundary = fractionFromConfig(
      model['scope'].speed_policy.formula_boundary_mph
    );
    var copy = model.public_copy.formula_fit;

    if (compare(speed, speedBoundary) > 0) {
      return formulaFit(maps, 'blue', 'green', [
        copy[1],
        copy[2]
      ]);
    }
    return formulaFit(maps, 'red', 'green', [copy[0], copy[2]]);
  }

  function allowed(model, field, value) {
    var config = model.input_contract.fields[field];
    return config && Array.isArray(config.values) && config.values.indexOf(value) !== -1;
  }

  function evaluate(authority, rules, input) {
    var model = authority.model;
    var exactData = authority.exactData;
    var maps = productMaps(rules);

    if (!input || typeof input !== 'object' || Array.isArray(input)) {
      return failure('human_review', ['missing_input']);
    }
    if (!allowed(model, 'device', input.device)) {
      return failure('human_review', ['unknown_device']);
    }
    var terminalOutcome = terminalOutcomeForAnswers(model, input);
    if (terminalOutcome) return terminalOutcome;
    if (!allowed(model, 'existing_sealant', input.existing_sealant)) {
      return failure('human_review', ['unknown_existing_sealant_state']);
    }
    if (!allowed(model, 'tire_change_state', input.tire_change_state)) {
      return failure('human_review', ['unknown_tire_change_state']);
    }
    if (typeof input.device_subtype !== 'string') {
      return failure('human_review', ['invalid_device_subtype']);
    }
    if (typeof input.device_model !== 'string') return failure('human_review', ['invalid_device_model']);
    if (typeof input.tire_marking !== 'string') return failure('human_review', ['invalid_tire_marking']);

    var speed;
    try {
      speed = measurementToUnit(model, input.top_speed, 'speed');
    } catch (error) {
      return failure('human_review', ['missing_or_implausible_speed']);
    }
    if (!within(speed, model['scope'].speed_policy.input_sanity_mph)) {
      return failure('human_review', ['implausible_or_unbounded_speed']);
    }

    if (input.selected_exact_row_id !== null) {
      if (!input.stock_tire_confirmation || input.tire_change_state !== 'stock') {
        return failure('human_review', ['exact_row_requires_confirmed_stock_tire']);
      }
      var row = exactData.rows.find(function findRow(candidate) {
        return candidate.row_id === input.selected_exact_row_id;
      });
      if (!row) return failure('human_review', ['selected_exact_row_not_released']);
      if (exactDeviceForInput(input.device) !== row.device) {
        return failure('human_review', ['selected_exact_row_device_contradiction']);
      }
      if (typeof input.device_model !== 'string' || !input.device_model.trim()) {
        return failure('human_review', ['invalid_device_model']);
      }
      if (typeof input.tire_marking !== 'string' || !input.tire_marking.trim()) {
        return failure('human_review', ['invalid_tire_marking']);
      }
      if (normalizeText(input.device_model) !== normalizeText(row.model)) {
        return failure('human_review', ['selected_exact_row_model_contradiction']);
      }
      if (normalizeText(input.tire_marking) !== normalizeText(row.tire)) {
        return failure('human_review', ['selected_exact_row_tire_contradiction']);
      }
      var supplied = checkedDimensions(model, input);
      if (supplied.error && supplied.error !== 'dimensions_not_supplied') {
        return failure('human_review', [supplied.error]);
      }
      if (!supplied.error && suppliedDimensionsContradictExactRow(row, supplied)) {
        return failure('human_review', ['selected_exact_row_dimension_contradiction']);
      }
      var sourceFormula = maps.handleToFormula[row.product_handle];
      if (!sourceFormula) return failure('human_review', ['selected_exact_row_product_unavailable']);
      var copy = model.public_copy.formula_fit;
      var fit = fitForCalculatedInput(model, maps, input, null, speed);
      fit.reasons = [
        'This estimate is based on the listed tire and works with Red, Blue, or Green.',
        compare(speed, fractionFromConfig(model['scope'].speed_policy.formula_boundary_mph)) <= 0
          ? copy[0]
          : copy[1],
        copy[2]
      ];
      return recommendation(
        'released_exact_row',
        toNumber(wholeOunceEstimate(model, parseCanonicalDecimal(String(row.recommended_amount_ounces)))),
        fit,
        [
          'released_exact_row_override',
          'practical_whole_ounce_estimate',
          'exact_amount_is_not_a_speed_certification'
        ],
        row.row_id
      );
    }

    var dimensions = checkedDimensions(model, input);
    if (dimensions.error) {
      return failure('human_review', [
        ['dimensions_not_supplied', 'incomplete_dimension_pair', 'missing_or_implausible_dimension'].indexOf(
          dimensions.error
        ) !== -1
          ? 'missing_or_implausible_dimension'
          : dimensions.error
      ]);
    }
    var calculatedFit = fitForCalculatedInput(
      model,
      maps,
      input,
      dimensions.width,
      speed
    );
    if (!calculatedFit) {
      return failure('human_review', ['formula_fit_requires_human_review']);
    }
    var rounded = roundedDimensionalRecommendation(
      model,
      dimensions.diameter,
      dimensions.width,
      input.device
    );
    return recommendation(
      'dimensional_model',
      toNumber(rounded),
      calculatedFit,
      ['owner_approved_dimensional_model', 'practical_whole_ounce_estimate']
    );
  }

  function setGroup(group, visible) {
    if (!group) return;
    group.hidden = !visible;
    if ('disabled' in group) group.disabled = !visible;
    group.querySelectorAll('input, select, textarea, button').forEach(function setDisabled(field) {
      field.disabled = !visible;
    });
  }

  function currentMatchMode(elements) {
    var checked = elements.form.querySelector('[data-finder-match-mode]:checked');
    return checked ? checked.value : '';
  }

  function exactRowsForDevice(authority, device) {
    var exactDevice = exactDeviceForInput(device);
    if (!exactDevice) return [];
    return authority.exactData.rows.filter(function matches(row) {
      return row.device === exactDevice;
    });
  }

  function findExactRow(authority, rowId) {
    return (
      authority.exactData.rows.find(function find(row) {
        return row.row_id === rowId;
      }) || null
    );
  }

  function clearResult(elements) {
    elements.result.hidden = true;
    elements.result.className = 'armor-finder-v2__result';
    elements.result.removeAttribute('data-finder-output');
    elements.result.removeAttribute('data-finder-formula');
    elements.result.removeAttribute('data-finder-source');
    elements.result.removeAttribute('data-finder-terminal');
    elements.result.textContent = '';
  }

  function clearErrors(elements) {
    elements.errors.hidden = true;
    elements.errors.textContent = '';
    elements.form.querySelectorAll('[aria-invalid="true"]').forEach(function clear(field) {
      field.removeAttribute('aria-invalid');
    });
  }

  function setInvalid(field) {
    if (field) field.setAttribute('aria-invalid', 'true');
  }

  function formatAmount(value) {
    return Number.isInteger(value) ? String(value) : Number(value).toFixed(1);
  }

  function revealResult(elements, focusResult) {
    elements.result.hidden = false;
    if (!focusResult) return;
    elements.result.focus({ preventScroll: true });
    var reducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    elements.result.scrollIntoView({
      behavior: reducedMotion ? 'auto' : 'smooth',
      block: 'start'
    });
  }

  function uiText(elements, key, fallback) {
    var dataKey =
      'ui' +
      key
        .split('-')
        .map(function capitalize(part) {
          return part.charAt(0).toUpperCase() + part.slice(1);
        })
        .join('');
    var value = elements.root.dataset[dataKey];
    return value && value.trim() ? value.trim() : fallback;
  }

  function configuredResultCopy(elements, key, fallback) {
    var copy = elements.root.querySelector('[data-finder-copy-key="' + key + '"]');
    if (!copy) return fallback;
    var title = copy.querySelector('[data-finder-copy-title]');
    var body = copy.querySelector('[data-finder-copy-body]');
    return {
      title: title && title.textContent.trim() ? title.textContent.trim() : fallback.title,
      body: body && body.textContent.trim() ? body.textContent.trim() : fallback.body
    };
  }

  function numberedUiText(elements, key, number, fallback) {
    return number + '. ' + uiText(elements, key, fallback).replace(/^\d+\.\s*/, '');
  }

  function purchasePlan(elements, output, tireCount, formula) {
    var node = elements.root.querySelector('[data-finder-purchase-options]');
    if (!node) return null;
    try {
      var config = JSON.parse(node.textContent);
      var total = output.recommended_amount.value * tireCount;
      var choices = (config.products[formula] || []).map(function plan(variant) {
        var minimum = Number(variant.min) || 1;
        var increment = Number(variant.increment) || 1;
        var quantity = Math.max(minimum, Math.ceil(total / variant.ounces));
        quantity = minimum + Math.ceil((quantity - minimum) / increment) * increment;
        return { variant: variant, quantity: quantity, total: total, supplied: quantity * variant.ounces,
          price: quantity * variant.price, config: config };
      }).filter(function available(plan) {
        return Number.isFinite(plan.supplied) && plan.supplied >= total && plan.quantity > 0 &&
          (!plan.variant.max || plan.quantity <= plan.variant.max);
      }).sort(function compare(a, b) {
        return a.supplied - b.supplied || a.price - b.price || a.quantity - b.quantity;
      });
      return choices[0] || null;
    } catch (error) { return null; }
  }

  function renderPurchase(elements, output, tireCount, formula) {
    var target = elements.result.querySelector('[data-finder-purchase]');
    if (!target) return;
    var total = output.recommended_amount.value * tireCount;
    var plan = purchasePlan(elements, output, tireCount, formula);
    var totalLine = '<p class="armor-finder-v2__total"><strong>About ' + escapeHtml(formatAmount(total)) +
      ' US fl oz total</strong> for ' + tireCount + (tireCount === 1 ? ' tire.' : ' matching tires.') + '</p>';
    if (!plan) {
      target.innerHTML = totalLine + '<p>Choose a bottle or kit with enough sealant for this total.</p>';
      return;
    }
    var price = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: plan.config.currency
    }).format(plan.price / 100);
    target.innerHTML = totalLine + '<p class="armor-finder-v2__bottles">' + plan.quantity +
      (plan.quantity === 1 ? ' bottle' : ' bottles') + ' × ' + escapeHtml(String(plan.variant.ounces)) +
      ' oz<br><span>Sealant + brass cap/tool</span></p>' +
      '<form method="post" action="' + escapeHtml(plan.config.action) + '" data-finder-cart-form>' +
      '<input type="hidden" name="id" value="' + escapeHtml(String(plan.variant.id)) + '">' +
      '<input type="hidden" name="quantity" value="' + plan.quantity + '">' +
      '<button class="armor-finder-v2__result-button" type="submit">Add to cart · ' + escapeHtml(price) + '</button></form>';
  }

  function renderSuccess(elements, authority, rules, output, refocusFormula) {
    var previousCountNode = elements.result.querySelector('[data-finder-tire-count]');
    var previousCount = previousCountNode
      ? Number(previousCountNode.value)
      : Number(elements.root.dataset.finderTireCount || 1);
    var recommendedFormula = output.formula_fit.preferred.formula;
    var suitableFormulas = recommendedFormula === 'red' ? ['red', 'blue', 'green'] : ['blue', 'green'];
    var preference = elements.root.dataset.finderFormulaPreference;
    var formula = suitableFormulas.indexOf(preference) !== -1 ? preference : recommendedFormula;
    var product = rules.products[formula];
    var amount = formatAmount(output.recommended_amount.value);
    var sourceLabel = output.source === 'released_exact_row'
      ? uiText(elements, 'exact-source', 'Based on the listed tire')
      : uiText(elements, 'measured-source', 'Estimated from your measurements');
    var explanations = {
      red: 'Our highest level of protection. Recommended at 30 mph or less.',
      blue: recommendedFormula === 'red'
        ? 'A great choice at this speed too. Protection as close to Red as we can get, with extra-long fibers for inner tubes.'
        : 'More flow for speeds above 30 mph, with protection as close to Red as we can get.',
      green: 'Spend less on your fill, especially with larger tires. Green works with tubes and tubeless tires.'
    };
    var countId = 'ArmorFinderCount-' + elements.root.dataset.instanceId;
    elements.result.className = 'armor-finder-v2__result armor-finder-v2__result--' + formula;
    elements.result.setAttribute('data-finder-output', 'recommendation');
    elements.result.setAttribute('data-finder-formula', formula);
    elements.result.setAttribute('data-finder-source', output.source);
    elements.result.removeAttribute('data-finder-terminal');
    var formulaChoice = '<label for="ArmorFinderFormula-' + escapeHtml(elements.root.dataset.instanceId) + '">Formula choice</label>' +
        '<select id="ArmorFinderFormula-' + escapeHtml(elements.root.dataset.instanceId) + '" data-finder-formula-choice>' +
        suitableFormulas.map(function option(candidate) {
          var label = candidate === recommendedFormula ? 'recommended' : candidate === 'green' ? 'best value' : 'also a great choice';
          return '<option value="' + candidate + '"' + (formula === candidate ? ' selected' : '') + '>' +
            escapeHtml(rules.products[candidate].name) + ' · ' + label + '</option>';
        }).join('') + '</select>';
    elements.result.innerHTML =
      '<p class="armor-finder-v2__result-kicker">Your formula</p><h2>' + escapeHtml(product.name) + '</h2>' +
      '<p class="armor-finder-v2__result-lede"><strong>About ' + escapeHtml(amount) + ' ' +
      escapeHtml(uiText(elements, 'amount-suffix', 'US fl oz per tire')) + '</strong></p>' +
      '<p class="armor-finder-v2__fit-note">These amounts are estimates. Stay close to the amount shown: a little more sealant gives more protection; a little less gives less.</p>' +
      '<p class="armor-finder-v2__fit-note">' + escapeHtml(explanations[formula]) + '</p>' +
      '<span class="armor-finder-v2__source-badge">' + escapeHtml(sourceLabel) + '</span>' +
      '<div class="armor-finder-v2__choice">' + formulaChoice + '</div>' +
      '<div class="armor-finder-v2__buy"><label for="' + countId + '">How many tires this size?</label>' +
      '<select id="' + countId + '" data-finder-tire-count>' +
      [1, 2, 3, 4, 6, 8].map(function option(count) { return '<option value="' + count + '"' + (count === previousCount ? ' selected' : '') + '>' + count + (count === 1 ? ' tire' : ' matching tires') + '</option>'; }).join('') +
      '</select><p class="armor-finder-v2__help">Different tire sizes? Check each size separately.</p>' +
      '<div data-finder-purchase aria-live="polite"></div></div>' +
      '<div class="armor-finder-v2__actions"><a class="armor-finder-v2__result-link" href="' + escapeHtml(product.url) + '">All ' +
      escapeHtml(product.name) + ' sizes and kits</a><a class="armor-finder-v2__result-link" href="' + INSTALL_URL +
      '">How to install it</a></div>';
    var formulaSelect = elements.result.querySelector('[data-finder-formula-choice]');
    if (formulaSelect) formulaSelect.addEventListener('change', function updateFormula(event) {
      elements.root.dataset.finderFormulaPreference = suitableFormulas.indexOf(event.target.value) !== -1 ? event.target.value : '';
      renderSuccess(elements, authority, rules, output, true);
    });
    elements.result.querySelector('[data-finder-tire-count]').addEventListener('change', function updatePurchase(event) {
      elements.root.dataset.finderTireCount = event.target.value;
      renderPurchase(elements, output, Number(event.target.value), formula);
    });
    renderPurchase(elements, output, previousCount, formula);
    revealResult(elements, !refocusFormula);
    if (refocusFormula) elements.result.querySelector('[data-finder-formula-choice]').focus();
  }

  function guidanceKey(reasonCodes) {
    var code = (reasonCodes || [])[0] || '';
    if (/authority_unavailable|selected_exact_row_product_unavailable/.test(code)) return 'unavailable';
    if (code === 'device_outside_current_scope') return 'road';
    if (/existing_sealant/.test(code)) return 'existing';
    if (code === 'exact_blue_requires_measured_red') return 'measured_red';
    if (code === 'exact_red_requires_measured_blue') return 'measured_blue';
    if (code === 'device_has_no_released_dimensional_envelope' || code === 'unallowlisted_device_subtype') return 'pev';
    if (code === 'invalid_device_model' || code === 'invalid_tire_marking') return 'exact';
    if (code === 'unknown_tire_change_state') return 'exact';
    if (/faster_scope|speed_scope|formula_fit_requires_human_review/.test(code)) return 'faster';
    if (/dimension|width_to_diameter|width_not_less/.test(code)) return 'dimensions';
    if (/speed/.test(code)) return 'speed';
    if (/exact_row/.test(code)) return 'exact';
    if (/device_subtype|unknown_device/.test(code)) return 'device';
    return 'general';
  }

  function renderFailure(elements, output, focusResult) {
    var key = guidanceKey(output.reason_codes);
    if (key === 'pev' && elements.device.value === 'euc') key = 'euc';
    var panelKey = key === 'measured_blue' ? 'measured_red' : key;
    var guidance = elements.root.querySelector('template[data-finder-guidance]');
    var panel = guidance && guidance.content.querySelector('[data-tire-help-topic="' + panelKey + '"]');
    var summary = panel && panel.querySelector('[data-help-title]');
    var intro = panel && panel.querySelector('[data-help-intro]');
    var steps = panel && panel.querySelector('[data-help-steps]');
    var targets = {
      existing: 'existing',
      dimensions: 'diameter', speed: 'speed', exact: 'measured', measured_red: 'measured', measured_blue: 'measured',
      faster: 'speed', euc: 'guide', device: 'device', pev: 'device', model: 'deviceModel', marking: 'tireMarking', road: null,
      unavailable: 'chart', general: 'device'
    };
    var target = targets[key];
    var links = { guide: INSTALL_URL, other: '/pages/tire-sealant-for-other-pneumatic-tires', chart: DOSING_URL };
    var label = panel ? panel.dataset.helpActionLabel : 'Read the tire setup guide';
    if (key === 'measured_blue') label = uiText(elements, 'exact-red-blue-action', 'Measure my tire');
    var action = !target ? '' : links[target]
      ? '<a class="armor-finder-v2__result-button" href="' + links[target] + '">' + escapeHtml(label) + '</a>'
      : '<button class="armor-finder-v2__result-button" type="button" data-finder-edit="' + target + '">' + escapeHtml(label) + '</button>';
    elements.result.className = 'armor-finder-v2__result armor-finder-v2__result--guidance';
    elements.result.setAttribute('data-finder-output', output.outcome);
    elements.result.setAttribute('data-finder-guidance', key);
    elements.result.removeAttribute('data-finder-terminal');
    elements.result.innerHTML =
      '<p class="armor-finder-v2__result-kicker">' + escapeHtml(uiText(elements, 'help-kicker', 'Your next steps')) + '</p>' +
      '<h2>' + escapeHtml(key === 'measured_blue' ? uiText(elements, 'exact-red-blue-heading', 'Check your tire measurements') : (summary ? summary.textContent.trim() : 'Check your tire setup')) + '</h2>' +
      '<p class="armor-finder-v2__result-copy">' + escapeHtml(key === 'measured_blue' ? uiText(elements, 'exact-red-blue-body', 'The listed amount works with Red, Blue or Green. Measure only if your fitted tire differs from the listing.') : (intro ? intro.textContent : 'Use the tire setup guide below to check the details before adding sealant.')) + '</p>';
    if (steps) elements.result.appendChild(steps.cloneNode(true));
    elements.result.insertAdjacentHTML('beforeend', '<div class="armor-finder-v2__actions">' + action + '</div>');
    revealResult(elements, focusResult);
  }

  function renderPreflight(elements, model, device) {
    if (['automobile', 'highway_motorcycle', 'e_moto'].indexOf(device) !== -1) {
      renderFailure(
        elements,
        failure('outside_current_scope', ['device_outside_current_scope']),
        false
      );
      return true;
    }
    return false;
  }

  function populateExactRows(elements, authority, device) {
    var allRows = exactRowsForDevice(authority, device);
    var terms = (elements.search ? elements.search.value : '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(/\s+/).filter(Boolean);
    var rows = allRows.filter(function matches(row) {
      var text = (row.model + ' ' + row.tire).toLowerCase().replace(/[^a-z0-9]/g, '');
      return terms.every(function match(term) { return text.indexOf(term) !== -1; });
    });
    var previousRowId = elements.exactRow.value;
    elements.exactRow.innerHTML =
      '<option value="">' +
      escapeHtml(uiText(elements, 'exact-row-prompt', 'Choose the confirmed setup')) +
      '</option>';
    rows.forEach(function appendRow(row) {
      var option = document.createElement('option');
      option.value = row.row_id;
      option.textContent = row.model + ' · ' + row.tire;
      elements.exactRow.appendChild(option);
    });
    if (
      previousRowId &&
      rows.some(function sameRow(row) {
        return row.row_id === previousRowId;
      })
    ) {
      elements.exactRow.value = previousRowId;
    } else {
      elements.stockConfirmation.checked = false;
    }
    if (elements.searchStatus) elements.searchStatus.textContent = rows.length ? rows.length + (rows.length === 1 ? ' listed tire. Check the model and tire marking below.' : ' listed tires. Choose the one fitted to your ride.') : 'No listed tire matches. Choose “Measure my tire” to use the tire fitted now.';
    return allRows;
  }

  function syncExactSource(elements, authority) {
    var row = findExactRow(authority, elements.exactRow.value);
    if (!row) {
      elements.exactSource.hidden = true;
      elements.exactSource.textContent = '';
      return;
    }
    elements.exactSource.textContent = 'Selected tire: ' + row.model + ', ' + row.tire + '.';
    elements.exactSource.hidden = false;
  }

  function clearExactSelection(elements, clearMode) {
    if (clearMode) {
      elements.matchModes.forEach(function uncheckMode(radio) {
        radio.checked = false;
      });
    }
    elements.exactRow.value = '';
    elements.stockConfirmation.checked = false;
    elements.exactSource.hidden = true;
    elements.exactSource.textContent = '';
  }

  function clearMeasuredAnswers(elements) {
    elements.deviceModel.value = '';
    elements.tireMarking.value = '';
    elements.diameter.value = '';
    elements.width.value = '';
  }

  function clearSpeedAnswer(elements) {
    elements.speed.value = '';
  }

  function clearPathAnswers(elements, clearMode) {
    clearExactSelection(elements, clearMode);
    clearMeasuredAnswers(elements);
    clearSpeedAnswer(elements);
  }

  function resetAfterDevice(elements) {
    if (elements.search) elements.search.value = '';
    elements.existing.value = '';
    clearPathAnswers(elements, true);
  }

  function resetAfterExisting(elements) {
    clearErrors(elements);
    clearResult(elements);
  }

  function resetForMatchMode(elements) {
    clearErrors(elements);
    clearResult(elements);
  }

  function currentTerminalOutcome(elements, model) {
    return terminalOutcomeForAnswers(model, {
      device: elements.device.value,
      existing_sealant: elements.existing.value
    });
  }

  function syncForm(elements, authority) {
    clearErrors(elements);
    clearResult(elements);
    var device = elements.device.value;
    var supported = ['onewheel', 'scooter', 'bicycle', 'e_bike', 'euc', 'other_pev'].indexOf(device) !== -1;
    [elements.setupFields, elements.exactChoice, elements.exactFields,
      elements.measuredFields, elements.speedFields].forEach(function hide(group) { setGroup(group, false); });
    elements.submit.hidden = true;
    if (!device) return;
    if (renderPreflight(elements, authority.model, device) || !supported) return;

    // Display the related checks together, and keep them editable even when one needs attention.
    setGroup(elements.setupFields, true);
    setGroup(elements.existingField, true);
    var terminalOutcome = currentTerminalOutcome(elements, authority.model);
    if (terminalOutcome) {
      renderFailure(elements, terminalOutcome, false);
      return;
    }
    var rows = populateExactRows(elements, authority, device);
    var hasExactChoices = rows.length > 0;
    setGroup(elements.exactChoice, hasExactChoices);
    if (hasExactChoices && !currentMatchMode(elements)) elements.matchModes[0].checked = true;
    var mode = hasExactChoices ? currentMatchMode(elements) : 'measured';
    elements.exactLegend.textContent = numberedUiText(elements, 'exact-legend', 2, 'Find your tire');
    setGroup(elements.exactFields, hasExactChoices && mode === 'exact');
    setGroup(elements.measuredFields, mode === 'measured');
    setGroup(elements.speedFields, true);
    elements.measuredLegend.textContent = numberedUiText(elements, 'measured-legend', 2, 'Measure your tire');
    elements.speedLegend.textContent = numberedUiText(elements, 'speed-legend', 3, 'How fast can it go?');
    elements.submit.hidden = false;
    if (mode === 'exact') syncExactSource(elements, authority);
  }

  function validateAndBuildInput(elements, authority) {
    clearErrors(elements);
    var errors = [];
    var firstInvalid = null;

    function requireValue(field, message) {
      if (!String(field.value || '').trim()) {
        errors.push(message);
        setInvalid(field);
        if (!firstInvalid) firstInvalid = field;
        return false;
      }
      return true;
    }

    var device = elements.device.value;
    if (!requireValue(elements.device, uiText(elements, 'error-device', 'Choose your ride.'))) {
      return { errors: errors, firstInvalid: firstInvalid };
    }

    var terminalOutcome = terminalOutcomeForAnswers(authority.model, {
      device: device,
      existing_sealant: elements.existing.value
    });
    if (terminalOutcome) {
      return { terminalOutput: terminalOutcome, errors: [] };
    }

    if (!requireValue(
      elements.existing,
      uiText(elements, 'error-existing', 'Confirm whether sealant is already inside.')
    )) {
      return { errors: errors, firstInvalid: firstInvalid };
    }
    terminalOutcome = currentTerminalOutcome(elements, authority.model);
    if (terminalOutcome) {
      return { terminalOutput: terminalOutcome, errors: [] };
    }

    var rows = exactRowsForDevice(authority, device);
    var mode = rows.length ? currentMatchMode(elements) : 'measured';
    if (rows.length && !mode) {
      errors.push(
        uiText(
          elements,
          'error-match-mode',
          'Choose a listed tire or select “Measure my tire.”'
        )
      );
      setInvalid(elements.matchModes[0]);
      if (!firstInvalid) firstInvalid = elements.matchModes[0];
    }

    var selectedRow = null;
    var diameter = null;
    var width = null;
    var modelName = '';
    var tireMarking = '';

    if (mode === 'exact') {
      if (requireValue(
        elements.exactRow,
        uiText(elements, 'error-exact-row', 'Choose the exact model and tire.')
      )) {
        selectedRow = findExactRow(authority, elements.exactRow.value);
        if (!selectedRow) {
          errors.push(
            uiText(elements, 'error-exact-unavailable', 'That tire is not in the current chart.')
          );
          setInvalid(elements.exactRow);
          if (!firstInvalid) firstInvalid = elements.exactRow;
        }
      }
      if (!elements.stockConfirmation.checked) {
        errors.push(
          uiText(
            elements,
            'error-stock',
            'Check the tire size, then tick the box to confirm it matches.'
          )
        );
        setInvalid(elements.stockConfirmation);
        if (!firstInvalid) firstInvalid = elements.stockConfirmation;
      }
      if (selectedRow) {
        modelName = selectedRow.model;
        tireMarking = selectedRow.tire;
      }
    } else if (mode === 'measured' || !rows.length) {
      modelName = elements.deviceModel.value.trim();
      tireMarking = elements.tireMarking.value.trim();
      diameter = canonicalDecimal(elements.diameter.value);
      width = canonicalDecimal(elements.width.value);
      if (!diameter || diameter === '0') {
        errors.push(
          uiText(elements, 'error-diameter', 'Enter the inflated tire’s full height as a number above zero.')
        );
        setInvalid(elements.diameter);
        if (!firstInvalid) firstInvalid = elements.diameter;
      }
      if (!width || width === '0') {
        errors.push(
          uiText(elements, 'error-width', 'Enter the inflated tire’s width as a number above zero.')
        );
        setInvalid(elements.width);
        if (!firstInvalid) firstInvalid = elements.width;
      }
    }

    var speed = canonicalDecimal(elements.speed.value);
    if (!speed || speed === '0') {
      errors.push(uiText(elements, 'error-speed', 'Enter your ride’s top speed as a number above zero.'));
      setInvalid(elements.speed);
      if (!firstInvalid) firstInvalid = elements.speed;
    }

    if (errors.length) {
      return {
        errors: errors,
        firstInvalid: firstInvalid
      };
    }

    var input = {
      device: device,
      device_subtype: '',
      device_model: modelName,
      tire_marking: tireMarking,
      top_speed: {
        value: speed,
        unit: elements.speedUnit.value
      },
      existing_sealant: elements.existing.value,
      selected_exact_row_id: selectedRow ? selectedRow.row_id : null,
      stock_tire_confirmation: Boolean(selectedRow && elements.stockConfirmation.checked),
      tire_change_state: selectedRow ? 'stock' : 'unknown'
    };
    if (!selectedRow) {
      input.outside_diameter = {
        value: diameter,
        unit: elements.dimensionUnit.value
      };
      input.installed_width = {
        value: width,
        unit: elements.dimensionUnit.value
      };
    }

    return {
      input: input,
      errors: []
    };
  }

  function showErrors(elements, validation) {
    elements.errors.innerHTML =
      '<strong>' +
      escapeHtml(uiText(elements, 'error-heading', 'Check these answers:')) +
      '</strong><ul>' +
      validation.errors
        .map(function errorItem(message) {
          return '<li>' + escapeHtml(message) + '</li>';
        })
        .join('') +
      '</ul>';
    elements.errors.hidden = false;
    if (validation.firstInvalid) validation.firstInvalid.focus();
  }

  function collectElements(root) {
    return {
      root: root,
      status: root.querySelector('[data-finder-status]'),
      loadError: root.querySelector('[data-finder-load-error]'),
      form: root.querySelector('[data-finder-form]'),
      errors: root.querySelector('[data-finder-errors]'),
      device: root.querySelector('[data-finder-device]'),
      setupFields: root.querySelector('[data-finder-setup-fields]'),
      setupLegend: root.querySelector('[data-finder-setup-legend]'),
      existingField: root.querySelector('[data-finder-existing-field]'),
      existing: root.querySelector('[data-finder-existing]'),
      exactChoice: root.querySelector('[data-finder-exact-choice]'),
      exactLegend: root.querySelector('[data-finder-exact-legend]'),
      matchModes: Array.prototype.slice.call(
        root.querySelectorAll('[data-finder-match-mode]')
      ),
      exactFields: root.querySelector('[data-finder-exact-fields]'),
      exactRow: root.querySelector('[data-finder-exact-row]'),
      search: root.querySelector('[data-finder-search]'),
      searchStatus: root.querySelector('[data-finder-search-status]'),
      stockConfirmation: root.querySelector('[data-finder-stock-confirmation]'),
      exactSource: root.querySelector('[data-finder-exact-source]'),
      measuredFields: root.querySelector('[data-finder-measured-fields]'),
      measuredLegend: root.querySelector('[data-finder-measured-legend]'),
      deviceModel: root.querySelector('[data-finder-device-model]'),
      tireMarking: root.querySelector('[data-finder-tire-marking]'),
      diameter: root.querySelector('[data-finder-diameter]'),
      width: root.querySelector('[data-finder-width]'),
      dimensionUnit: root.querySelector('[data-finder-dimension-unit]'),
      speedFields: root.querySelector('[data-finder-speed-fields]'),
      speedLegend: root.querySelector('[data-finder-speed-legend]'),
      speed: root.querySelector('[data-finder-speed]'),
      speedUnit: root.querySelector('[data-finder-speed-unit]'),
      submit: root.querySelector('[data-finder-submit]'),
      result: root.querySelector('[data-finder-result]')
    };
  }

  function hasRequiredElements(elements) {
    return Object.keys(elements).every(function present(key) {
      if (key === 'search' || key === 'searchStatus') return true;
      if (key === 'matchModes') return elements[key].length === 2;
      return Boolean(elements[key]);
    });
  }

  function initialize(root) {
    if (!root || instances.has(root)) return;
    var elements = collectElements(root);
    if (!hasRequiredElements(elements)) return;

    var controller = new AbortController();
    var signal = controller.signal;
    var state = {
      authority: null,
      rules: null
    };

    function resetOnEdit(event) {
      clearErrors(elements);
      if (
        event.type === 'change' &&
        event.target.matches(
          [
            '[data-finder-device]',
            '[data-finder-existing]',
            '[data-finder-match-mode]',
            '[data-finder-exact-row]'
          ].join(', ')
        )
      ) {
        return;
      }
      clearResult(elements);
    }

    function onDeviceChange() {
      var modelContext = root.querySelector('[data-finder-model-context]');
      if (modelContext) modelContext.hidden = true;
      delete elements.root.dataset.finderFormulaPreference;
      delete elements.root.dataset.finderTireCount;
      resetAfterDevice(elements);
      syncForm(elements, state.authority);
    }

    function onExistingChange() {
      resetAfterExisting(elements);
      syncForm(elements, state.authority);
    }

    function onMatchModeChange() {
      delete elements.root.dataset.finderFormulaPreference;
      resetForMatchMode(elements);
      syncForm(elements, state.authority);
    }

    function onExactRowChange() {
      clearErrors(elements);
      clearResult(elements);
      elements.stockConfirmation.checked = false;
      syncExactSource(elements, state.authority);
    }

    function onSubmit(event) {
      event.preventDefault();
      clearResult(elements);
      var validation = validateAndBuildInput(elements, state.authority);
      if (validation.errors.length) {
        showErrors(elements, validation);
        return;
      }
      if (validation.terminalOutput) {
        renderFailure(elements, validation.terminalOutput, true);
        return;
      }
      var output;
      try {
        output = evaluate(state.authority, state.rules, validation.input);
      } catch (error) {
        renderFailure(elements, failure('human_review', ['model_authority_unavailable']), true);
        return;
      }
      if (output.outcome === 'recommendation') {
        renderSuccess(elements, state.authority, state.rules, output);
      } else {
        renderFailure(elements, output, true);
      }
    }

    function onResultClick(event) {
      var trigger = event.target.closest('[data-finder-edit], [data-finder-measure-instead]');
      if (!trigger) return;
      if (trigger.hasAttribute('data-finder-green-preference')) {
        elements.root.dataset.finderFormulaPreference = 'green';
      }
      var target = trigger.hasAttribute('data-finder-measure-instead') ? 'measured' : trigger.dataset.finderEdit;
      if (target === 'measured') {
        var selectedRow = findExactRow(state.authority, elements.exactRow.value);
        if (selectedRow && elements.stockConfirmation.checked) {
          if (!elements.deviceModel.value) elements.deviceModel.value = selectedRow.model;
          if (!elements.tireMarking.value) elements.tireMarking.value = selectedRow.tire;
        }
        var measuredMode = elements.matchModes.find(function findMeasured(radio) {
          return radio.value === 'measured';
        });
        if (measuredMode) measuredMode.checked = true;
        clearErrors(elements);
        clearResult(elements);
        syncForm(elements, state.authority);
        elements.diameter.focus();
        return;
      }
      var field = elements[target];
      if (field && !field.disabled) {
        field.focus();
        field.scrollIntoView({ block: 'center', behavior: 'auto' });
      }
    }

    instances.set(root, {
      destroy: function destroy() {
        controller.abort();
        elements.form.hidden = true;
        elements.loadError.hidden = true;
        elements.status.hidden = false;
        elements.status.classList.remove('visually-hidden');
        elements.status.textContent = uiText(
          elements,
          'loading',
          'Loading the tire chart and calculator…'
        );
        clearErrors(elements);
        clearResult(elements);
        instances.delete(root);
      }
    });

    elements.form.noValidate = true;
    elements.form.addEventListener('input', resetOnEdit, { signal: signal });
    elements.form.addEventListener('change', resetOnEdit, { signal: signal });
    elements.device.addEventListener('change', onDeviceChange, { signal: signal });
    elements.existing.addEventListener('change', onExistingChange, { signal: signal });
    elements.matchModes.forEach(function listen(radio) {
      radio.addEventListener('change', onMatchModeChange, { signal: signal });
    });
    elements.exactRow.addEventListener('change', onExactRowChange, { signal: signal });
    elements.form.addEventListener('submit', onSubmit, { signal: signal });
    elements.result.addEventListener('click', onResultClick, { signal: signal });
    elements.form.addEventListener('click', onResultClick, { signal: signal });
    if (elements.search) elements.search.addEventListener('input', function searchRows() {
      clearResult(elements);
      populateExactRows(elements, state.authority, elements.device.value);
      syncExactSource(elements, state.authority);
    }, { signal: signal });
    var retry = root.querySelector('[data-finder-retry]');
    if (retry) retry.addEventListener('click', function retryLoad() {
      instances.get(root).destroy();
      initialize(root);
    }, { signal: signal });

    loadAndVerifyAuthority(root)
      .then(async function authorityReady(authority) {
        if (signal.aborted) return;
        var rules = formulaRulesFor(root);
        await verifyFormulaMirror(root, authority, rules);
        if (signal.aborted) return;
        state.authority = authority;
        state.rules = rules;
        elements.loadError.hidden = true;
        elements.form.hidden = false;
        elements.status.textContent = uiText(
          elements,
          'ready',
          'Choose a listed tire or measure yours.'
        );
        var query = new URLSearchParams(window.location.search);
        var prefillDevice = query.get('device');
        var prefillMode = query.get('mode');
        if (!elements.device.value && (prefillMode === null || ['exact', 'measured'].indexOf(prefillMode) !== -1) &&
          ['onewheel', 'scooter', 'bicycle', 'e_bike', 'euc', 'other_pev'].indexOf(prefillDevice) !== -1) {
          elements.device.value = prefillDevice;
          if (prefillMode === 'measured') {
            elements.deviceModel.value = (query.get('model') || '').slice(0, 160);
            var modelContext = root.querySelector('[data-finder-model-context]');
            if (modelContext && elements.deviceModel.value) {
              var stockTire = (query.get('tire') || '').slice(0, 200);
              modelContext.textContent = elements.deviceModel.value + (stockTire ? ' — stock tire: ' + stockTire + '. Measure the fitted tire below.' : ' — measure the fitted tire below.');
              modelContext.hidden = false;
            }
            elements.matchModes[1].checked = true;
          } else if (prefillMode === 'exact') {
            var prefillRow = findExactRow(authority, query.get('row'));
            if (prefillRow && prefillRow.device === exactDeviceForInput(prefillDevice)) {
              populateExactRows(elements, authority, prefillDevice);
              elements.exactRow.value = prefillRow.row_id;
              elements.stockConfirmation.checked = false;
              elements.matchModes[0].checked = true;
            }
          }
        }
        elements.status.classList.add('visually-hidden');
        syncForm(elements, authority);
      })
      .catch(function authorityFailed() {
        if (signal.aborted) return;
        elements.form.hidden = true;
        elements.status.textContent = uiText(
          elements,
          'load-failed',
          'The calculator did not load. Use the full tire chart.'
        );
        elements.loadError.hidden = false;
        clearResult(elements);
      });
  }

  function refresh(container) {
    rootsWithin(container).forEach(initialize);
  }

  function destroy(container) {
    rootsWithin(container).forEach(function destroyRoot(root) {
      var instance = instances.get(root);
      if (instance) instance.destroy();
    });
  }

  window[GLOBAL_KEY] = {
    evaluate: evaluate,
    refresh: refresh,
    destroy: destroy
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function initializeDocument() {
        refresh(document);
      },
      { once: true }
    );
  } else {
    refresh(document);
  }

  document.addEventListener('shopify:section:load', function initializeShopifySection(event) {
    refresh(event.target);
  });

  document.addEventListener('shopify:section:unload', function destroyShopifySection(event) {
    destroy(event.target);
  });
})();
